// src/pages/checkout/CheckoutPage.tsx
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import { http } from "../../services/http";
import { useAuth } from "../../contexts/AuthProvider";
import { useCart } from "../../contexts/CartProvider";
import { useTranslation } from "react-i18next";

/* ================= TYPES ================= */
// type AnyCart = {
//   items?: unknown[];
//   clear?: () => Promise<void> | void;
// };

type CartItem = {
  variantId: number | string; // UI = cartId
  quantity: number;
  unitPrice?: number;
  productName?: string;
};

type VoucherDto = {
  code: string;
  label?: string;
  type: "percent" | "fixed" | "freeship";
  value: number;
  minOrder?: number | null;
  maxDiscount?: number | null;
  isActive?: boolean;

  // extra (optional)
  endDate?: string | Date | null;
  note?: string | null;
  source?: "owned" | "public";
};

type CheckoutConfig = {
  shipping: {
    fee: number;
    freeShipMin: number;
  };
  points: {
    rate: number; // VND per point
    maxPercent: number;
  };
  vouchers: VoucherDto[];
};

type AppliedVoucher = VoucherDto | null;

type UserProfileResponse = {
  user?: any;
  pointValue?: number;
  availablePoints?: number;
  pointToMoneyRate?: number; // VND per point
  vouchers?: Array<{
    couponId?: number;
    code?: string;
    type?: string | null;
    isFreeShipping?: boolean | null;
    discountPercentage?: number | null;
    discountAmount?: number | null;
    maxDiscountAmount?: number | null;
    minOrderValue?: number | null;
    startDate?: string | Date | null;
    endDate?: string | Date | null;
    note?: string | null;
    voucherStatus?: string | null;
  }>;
};

/* ================= HELPERS ================= */
function isCartItem(x: unknown): x is CartItem {
  return (
    typeof x === "object" && x !== null && "variantId" in x && "quantity" in x
  );
}

function safeNumber(v: unknown, fallback: number) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function normCode(s: string) {
  return String(s ?? "")
    .trim()
    .toUpperCase();
}

function findVoucherByCode(
  vouchers: VoucherDto[],
  code: string,
): VoucherDto | null {
  const c = normCode(code);
  if (!c) return null;
  return vouchers.find((v) => normCode(v.code) === c) ?? null;
}

function calcVoucherDiscount(subtotal: number, v: VoucherDto | null): number {
  if (!v) return 0;
  const minOrder = v.minOrder ?? 0;
  if (subtotal < minOrder) return 0;

  if (v.type === "percent") {
    const raw = Math.floor((subtotal * v.value) / 100);
    const capped = v.maxDiscount != null ? Math.min(raw, v.maxDiscount) : raw;
    return Math.max(0, capped);
  }

  if (v.type === "fixed") {
    return Math.max(0, Math.min(subtotal, v.value));
  }

  return 0; // freeship xử lý ở shipping
}

/* ================= DEFAULT CONFIG (FALLBACK) ================= */
const DEFAULT_CONFIG: CheckoutConfig = {
  shipping: { fee: 35000, freeShipMin: 0 },
  points: { rate: 1000, maxPercent: 30 },
  vouchers: [], // ✅ đừng hardcode OWL10/FREESHIP ở đây nữa
};

export default function CheckoutPage() {
  const location = useLocation();
  const hasCancelled = useRef(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useCart();

  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "vi";
  const moneyLocale = lang === "en" ? "en-US" : "vi-VN";

  const formatMoney = (n: number) =>
    (Number.isFinite(n) ? n : 0).toLocaleString(moneyLocale, {
      maximumFractionDigits: 0,
    }) + "₫";

  const formatDateShort = (d?: string | Date | null) => {
    if (!d) return "";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString(moneyLocale);
  };

  /* ================= EP LOGIN ================= */
  // useEffect(() => {
  //   const q = new URLSearchParams(location.search);
  //   const orderIdFromQuery = q.get("orderId") || q.get("orderCode");
  //   const isCancel = q.get("cancel");

  //   if (isCancel === "true" && orderIdFromQuery && !hasCancelled.current) {
  //     hasCancelled.current = true;

  //     // ✅ đổi endpoint cancel cho đúng backend bạn
  //     http
  //       .post(`/api/Order/cancel/${orderIdFromQuery}`)
  //       .finally(() => navigate("/cart", { replace: true }));
  //   }
  // }, [location.search, navigate]);

  useEffect(() => {
    if (!user) navigate("/auth", { replace: true });
  }, [user, navigate]);

  /* ================= CART ================= */
  const items: CartItem[] = useMemo(() => {
    return (cart.items ?? []).filter(isCartItem).map((i) => ({
      ...i,
      quantity: Number(i.quantity) || 0,
      unitPrice: Number((i as any).unitPrice) || 0,
    }));
  }, [cart.items]);

  const cartIds = useMemo(() => {
    return items
      .map((i) => Number(i.variantId))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [items]);

  const subtotal = useMemo(() => {
    return items.reduce(
      (s, i) => s + (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0),
      0,
    );
  }, [items]);

  /* ================= CONFIG (OPTIONAL) ================= */
  const [config, setConfig] = useState<CheckoutConfig>(DEFAULT_CONFIG);
  const [cfgLoading, setCfgLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setCfgLoading(true);
        const res = await http.get("/settings/checkout");
        const data = res?.data ?? {};

        const next: CheckoutConfig = {
          shipping: {
            fee: safeNumber(data?.shipping?.fee, DEFAULT_CONFIG.shipping.fee),
            freeShipMin: safeNumber(
              data?.shipping?.freeShipMin,
              DEFAULT_CONFIG.shipping.freeShipMin,
            ),
          },
          points: {
            rate: safeNumber(data?.points?.rate, DEFAULT_CONFIG.points.rate),
            maxPercent: safeNumber(
              data?.points?.maxPercent,
              DEFAULT_CONFIG.points.maxPercent,
            ),
          },
          vouchers:
            Array.isArray(data?.vouchers) && data.vouchers.length > 0
              ? data.vouchers
              : DEFAULT_CONFIG.vouchers,
        };

        if (mounted) setConfig((prev) => ({ ...prev, ...next }));
      } catch {
        // ignore -> dùng DEFAULT_CONFIG
      } finally {
        if (mounted) setCfgLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  /* ================= SHIPPING FORM ================= */
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");

  /* ================= VOUCHER ================= */
  const [voucher, setVoucher] = useState<AppliedVoucher>(null);
  const [voucherCodeInput, setVoucherCodeInput] = useState("");

  /* ================= PROFILE: POINTS + OWNED VOUCHERS ================= */
  const [userPoints, setUserPoints] = useState<number>(0);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileErr, setProfileErr] = useState<string>("");

  const [ownedVouchers, setOwnedVouchers] = useState<VoucherDto[]>([]);

  async function getProfile(): Promise<UserProfileResponse> {
    try {
      // baseURL không có /api
      const r1 = await http.get("/api/Users/profile");
      return r1?.data ?? {};
    } catch {
      // baseURL đã là /api
      const r2 = await http.get("/Users/profile");
      return r2?.data ?? {};
    }
  }

  async function createPayOSPaymentLink(
    orderId: number | string,
  ): Promise<string> {
    const oid = String(orderId);

    // ✅ URL PayOS redirect khi thanh toán thành công
    const CancelUrl = `${window.location.origin}/payment/callback?success=true&orderId=${encodeURIComponent(oid)}`;
    const ReturnUrl = `${window.location.origin}/payment/callback?cancel=true&orderId=${encodeURIComponent(oid)}`;


    // ✅ Gọi đúng endpoint backend của bạn
    const r = await http.post("/api/QR/create-payment-link", {
      orderId: Number.isFinite(Number(orderId)) ? Number(orderId) : oid,
      ReturnUrl,
      CancelUrl,
    });

    // ✅ Lấy checkoutUrl (tuỳ backend trả về bọc data hay không)
    const url =
      r?.data?.checkoutUrl ||
      r?.data?.data?.checkoutUrl ||
      r?.data?.checkoutUrl?.url ||
      r?.data?.data?.checkoutUrl?.url;

    if (!url) throw new Error("Missing checkoutUrl");
    return url;
  }

  useEffect(() => {
    let mounted = true;

    (async () => {
      setProfileErr("");
      if (!user) return;

      try {
        setProfileLoading(true);
        const data = await getProfile();

        const availablePoints = safeNumber(data?.availablePoints, 0);
        const pointToMoneyRate = safeNumber(data?.pointToMoneyRate, 0);

        if (!mounted) return;

        setUserPoints(availablePoints);

        // đồng bộ rate từ backend (VND per point)
        if (pointToMoneyRate > 0) {
          setConfig((prev) => ({
            ...prev,
            points: { ...prev.points, rate: pointToMoneyRate },
          }));
        }

        // map voucher owned (VoucherUsage + Coupons)
        const mapped: VoucherDto[] = (data?.vouchers ?? [])
          .map((x) => {
            const code = normCode(x?.code ?? "");
            if (!code) return null;

            // ✅ ưu tiên type từ backend
            const rawType = normCode(x?.type ?? "");
            const isFreeShip =
              x?.isFreeShipping === true ||
              rawType === "FREESHIP" ||
              rawType === "FREE_SHIP" ||
              rawType === "FREE-SHIP";

            const percent = safeNumber(
              x?.discountPercentage,
              safeNumber((x as any)?.value, 0),
            );
            const amount = safeNumber(
              x?.discountAmount,
              safeNumber((x as any)?.value, 0),
            );

            const maxDiscount =
              x?.maxDiscountAmount == null
                ? null
                : safeNumber(x?.maxDiscountAmount, 0);

            const minOrder =
              x?.minOrderValue == null ? 0 : safeNumber(x?.minOrderValue, 0);

            const type: VoucherDto["type"] = isFreeShip
              ? "freeship"
              : percent > 0
                ? "percent"
                : "fixed";

            // ✅ freeship không cần value để tính discount (bạn xử lý ở shippingFee)
            const value =
              type === "percent" ? percent : type === "fixed" ? amount : 0;

            // i18n label fallback
            const label =
              x?.note?.trim() ||
              (type === "freeship"
                ? t("checkout.freeShippingLabel")
                : type === "percent"
                  ? t("checkout.discountPercent", { value })
                  : t("checkout.discountFixed", { value: formatMoney(value) }));

            return {
              code,
              label,
              type,
              value,
              minOrder,
              maxDiscount: type === "percent" ? maxDiscount : null,
              isActive: true,
              endDate: x?.endDate ?? null,
              note: x?.note ?? null,
              source: "owned",
            } as VoucherDto;
          })
          .filter(Boolean) as VoucherDto[];

        // unique by code
        const uniq = new Map<string, VoucherDto>();
        for (const v of mapped) uniq.set(normCode(v.code), v);

        setOwnedVouchers(Array.from(uniq.values()));
      } catch (e: any) {
        if (!mounted) return;
        setUserPoints(0);
        setOwnedVouchers([]);

        const data = e?.response?.data;

        const msg =
          (typeof data?.message === "string" && data.message) ||
          (typeof data?.error === "string" && data.error) ||
          (typeof data === "string" && data) ||
          (typeof e?.message === "string" && e.message) ||
          t("checkout.profileFail");

        setProfileErr(msg);
      } finally {
        if (mounted) setProfileLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, t]); // t đổi theo ngôn ngữ

  // gộp voucher: ưu tiên owned, rồi public
  const allVouchers = useMemo(() => {
    const map = new Map<string, VoucherDto>();
    for (const v of ownedVouchers) map.set(normCode(v.code), v);
    for (const v of config.vouchers ?? []) {
      const key = normCode(v.code);
      if (!map.has(key)) map.set(key, { ...v, source: v.source ?? "public" });
    }
    return Array.from(map.values());
  }, [ownedVouchers, config.vouchers]);

  const eligibleVouchers = useMemo(() => {
    return allVouchers.filter((v) => subtotal >= (v.minOrder ?? 0));
  }, [allVouchers, subtotal]);

  const applyVoucherCode = () => {
    const found = findVoucherByCode(allVouchers, voucherCodeInput);
    if (!found) return false;
    if (subtotal < (found.minOrder ?? 0)) return false;
    setVoucher(found);
    return true;
  };

  const voucherDiscount = useMemo(
    () => calcVoucherDiscount(subtotal, voucher),
    [subtotal, voucher],
  );

  /* ================= POINTS (NO CHECKBOX) ================= */
  const [pointsInput, setPointsInput] = useState<number>(0);

  const maxPointsCanUse = useMemo(() => {
    if (userPoints <= 0) return 0;

    const base = Math.max(0, subtotal - voucherDiscount);

    const maxByPercentVnd = Math.floor(
      (subtotal * config.points.maxPercent) / 100,
    );
    const maxDiscountVnd = Math.min(base, maxByPercentVnd);

    const rate = config.points.rate || 1;
    const maxByVndToPoints = Math.floor(maxDiscountVnd / rate);

    return Math.max(0, Math.min(userPoints, maxByVndToPoints));
  }, [
    subtotal,
    voucherDiscount,
    config.points.maxPercent,
    config.points.rate,
    userPoints,
  ]);

  useEffect(() => {
    // auto clamp khi max thay đổi
    setPointsInput((prev) =>
      Math.max(0, Math.min(Number(prev) || 0, maxPointsCanUse)),
    );
  }, [maxPointsCanUse]);

  const pointsToUse = useMemo(() => {
    return Math.max(0, Math.min(pointsInput, maxPointsCanUse));
  }, [pointsInput, maxPointsCanUse]);

  const pointDiscount = useMemo(() => {
    if (pointsToUse <= 0) return 0;
    const base = Math.max(0, subtotal - voucherDiscount);
    const rate = config.points.rate || 1;
    return Math.min(base, pointsToUse * rate);
  }, [pointsToUse, subtotal, voucherDiscount, config.points.rate]);

  /* ================= SHIPPING ================= */
  const shippingFee = useMemo(() => {
    if (!address.trim()) return 0;
    if (voucher?.type === "freeship") return 0;
    if (
      (config.shipping.freeShipMin ?? 0) > 0 &&
      subtotal >= config.shipping.freeShipMin
    )
      return 0;
    return safeNumber(config.shipping.fee, 35000);
  }, [
    address,
    voucher,
    subtotal,
    config.shipping.freeShipMin,
    config.shipping.fee,
  ]);

  /* ================= TOTAL ================= */
  const total = useMemo(() => {
    const tt = subtotal - voucherDiscount - pointDiscount + shippingFee;
    return Math.max(0, tt);
  }, [subtotal, voucherDiscount, pointDiscount, shippingFee]);

  /* ================= SUBMIT ================= */
  const [paymentMethod, setPaymentMethod] = useState<"COD" | "TRANSFER">("COD");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canCheckout = useMemo(() => {
    return (
      !loading &&
      cartIds.length > 0 &&
      fullName.trim().length >= 2 &&
      email.trim().includes("@") &&
      phone.trim().length >= 8 &&
      address.trim().length >= 8
    );
  }, [loading, cartIds.length, fullName, email, phone, address]);

  const onCheckout = async () => {
    setError("");

    if (cartIds.length === 0) {
      setError(t("checkout.errorEmptyCart"));
      return;
    }

    try {
      setLoading(true);

      const finalNote = [
        `Receiver: ${fullName.trim()} | Phone: ${phone.trim()} | Email: ${email.trim()}`,
        voucher ? `Voucher: ${voucher.code}` : "",
        pointsToUse > 0
          ? `Use points: ${pointsToUse} (≈ ${formatMoney(pointDiscount)})`
          : "",
        address.trim()
          ? `Shipping fee: ${shippingFee === 0 ? "Free" : formatMoney(shippingFee)}`
          : "",
        note.trim(),
      ]
        .filter(Boolean)
        .join("\n");

      const payload: any = {
        cartIds,
        paymentMethod: paymentMethod === "TRANSFER" ? "transfer" : "COD",

        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),

        streetAddress: address.trim(),
        city: "0",
        district: "0",
        ward: "0",

        additionalInfo: finalNote,
        voucherCode: voucher?.code ? normCode(voucher.code) : null,

        // points
        usePoints: pointsToUse > 0,
        pointsToUse: pointsToUse > 0 ? pointsToUse : 0,
      };

      const res = await http.post("/api/Order/create", payload);
      const orderId = res?.data?.orderId;
      if (!orderId) throw new Error("Missing orderId");

      // ✅ TRANSFER -> redirect PayOS
      if (paymentMethod === "TRANSFER") {
        const checkoutUrl = await createPayOSPaymentLink(orderId);

        // ⚠️ khuyên: đừng clear cart ở đây (vì user có thể cancel payment)
        window.location.href = checkoutUrl;
        return;
      }

      // ✅ COD -> clear cart + redirect profile
      cart.clearLocal();
      navigate(`/orders/${orderId}`, { replace: true });
    } catch (e: any) {
      const data = e?.response?.data;

      const msg =
        (typeof data?.message === "string" && data.message) ||
        (typeof data?.error === "string" && data.error) ||
        (typeof data === "string" && data) ||
        (typeof e?.message === "string" && e.message) ||
        t("checkout.errorCheckoutFail");

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-[#f6f3ef]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        {/* HEADER */}
        <div className="mb-10 sm:mb-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.35em] text-neutral-500 uppercase">
                {t("checkout.brand")}
              </p>
              <h1 className="text-3xl sm:text-4xl font-semibold mt-3">
                {t("checkout.title")}
              </h1>
              <p className="text-sm text-neutral-500 mt-2">
                {t("checkout.subtitle")}
              </p>
            </div>

            <Link
              to="/cart"
              className="text-xs uppercase tracking-[0.25em] text-neutral-700 hover:text-black underline underline-offset-4"
            >
              {t("checkout.backToCart")}
            </Link>
          </div>

          {cfgLoading ? (
            <p className="text-xs text-neutral-500 mt-4">
              {t("checkout.loadingConfig")}
            </p>
          ) : null}
        </div>

        {error && (
          <div className="mb-6 rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* 3 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* COL 1: SHIPPING */}
          <div className="lg:col-span-5 space-y-6">
            <section className="bg-white rounded-2xl shadow-sm p-6 sm:p-7 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium">
                  {t("checkout.shippingInfo")}
                </h2>
                <span className="text-xs text-neutral-500">
                  {t("checkout.required")}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                    {t("checkout.fullName")}
                  </label>
                  <input
                    placeholder={t("checkout.fullNamePh")}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-12 w-full rounded-xl border border-neutral-200 px-4 text-sm outline-none focus:border-black"
                    autoComplete="name"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                    {t("checkout.email")}
                  </label>
                  <input
                    placeholder={t("checkout.emailPh")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 w-full rounded-xl border border-neutral-200 px-4 text-sm outline-none focus:border-black"
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                    {t("checkout.phone")}
                  </label>
                  <input
                    placeholder={t("checkout.phonePh")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-12 w-full rounded-xl border border-neutral-200 px-4 text-sm outline-none focus:border-black"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                    {t("checkout.shippingFee")}
                  </label>

                  <div className="h-12 w-full rounded-xl border border-neutral-200 px-4 text-sm flex items-center justify-between">
                    <span className="text-neutral-700">
                      {!address.trim()
                        ? t("checkout.enterAddressToCalc")
                        : shippingFee === 0
                          ? t("checkout.free")
                          : formatMoney(shippingFee)}
                    </span>
                    <span className="text-xs text-neutral-400">
                      {t("checkout.auto")}
                    </span>
                  </div>

                  <p className="text-xs text-neutral-500">
                    {t("checkout.defaultShipping", {
                      fee: formatMoney(config.shipping.fee || 35000),
                    })}
                    {config.shipping.freeShipMin > 0
                      ? t("checkout.freeshipFrom", {
                          min: formatMoney(config.shipping.freeShipMin),
                        })
                      : ""}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                  {t("checkout.address")}
                </label>
                <textarea
                  placeholder={t("checkout.addressPh")}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm outline-none focus:border-black"
                  autoComplete="street-address"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                  {t("checkout.note")}
                </label>
                <textarea
                  placeholder={t("checkout.notePh")}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm outline-none focus:border-black"
                />
              </div>
            </section>
          </div>

          {/* COL 2: VOUCHER + POINTS */}
          <div className="lg:col-span-4 space-y-6">
            {/* VOUCHER */}
            <section className="bg-white rounded-2xl shadow-sm p-6 sm:p-7 space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-medium">
                    {t("checkout.voucher")}
                  </h2>
                  <p className="text-sm text-neutral-500 mt-1">
                    {ownedVouchers.length > 0
                      ? t("checkout.voucherOwnedHint")
                      : t("checkout.voucherNoOwnedHint")}
                  </p>
                </div>

                {voucher ? (
                  <button
                    type="button"
                    onClick={() => setVoucher(null)}
                    className="text-xs uppercase tracking-[0.25em] text-neutral-700 hover:text-black underline underline-offset-4"
                  >
                    {t("checkout.removeVoucher")}
                  </button>
                ) : null}
              </div>

              <div className="flex gap-3">
                <input
                  value={voucherCodeInput}
                  onChange={(e) => setVoucherCodeInput(e.target.value)}
                  placeholder={t("checkout.voucherInputPh")}
                  className="h-12 flex-1 rounded-xl border border-neutral-200 px-4 text-sm outline-none focus:border-black"
                />
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    const ok = applyVoucherCode();
                    if (!ok) setError(t("checkout.errorInvalidVoucher"));
                  }}
                  className="h-12 rounded-xl bg-black px-5 text-xs uppercase tracking-[0.25em] text-white hover:opacity-90"
                >
                  {t("checkout.apply")}
                </button>
              </div>

              {eligibleVouchers.length > 0 ? (
                <div className="grid gap-3">
                  {eligibleVouchers.map((v) => {
                    const active =
                      normCode(voucher?.code ?? "") === normCode(v.code);

                    const badge =
                      v.type === "percent"
                        ? `-${v.value}%`
                        : v.type === "fixed"
                          ? `-${formatMoney(v.value)}`
                          : t("checkout.freeShip");

                    return (
                      <button
                        key={v.code}
                        type="button"
                        onClick={() => setVoucher(v)}
                        className={`text-left rounded-2xl border px-5 py-4 transition ${
                          active
                            ? "border-black bg-black text-white"
                            : "border-neutral-200 hover:border-neutral-400 bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{normCode(v.code)}</p>
                              {v.source === "owned" ? (
                                <span
                                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                    active
                                      ? "border-white/30 text-white/90"
                                      : "border-neutral-200 text-neutral-600"
                                  }`}
                                >
                                  {t("checkout.yours")}
                                </span>
                              ) : null}
                            </div>

                            <p
                              className={`text-xs mt-1 ${
                                active ? "text-neutral-300" : "text-neutral-500"
                              }`}
                            >
                              {v.label ?? t("checkout.promo")}
                            </p>

                            {(v.minOrder ?? 0) > 0 ? (
                              <p
                                className={`text-xs mt-2 ${
                                  active
                                    ? "text-neutral-300"
                                    : "text-neutral-500"
                                }`}
                              >
                                {t("checkout.minOrder", {
                                  money: formatMoney(v.minOrder ?? 0),
                                })}
                              </p>
                            ) : null}

                            {v.endDate ? (
                              <p
                                className={`text-xs mt-2 ${
                                  active
                                    ? "text-neutral-300"
                                    : "text-neutral-500"
                                }`}
                              >
                                {t("checkout.exp", {
                                  date: formatDateShort(v.endDate),
                                })}
                              </p>
                            ) : null}
                          </div>

                          <span
                            className={`text-xs font-medium ${
                              active ? "text-white" : "text-neutral-700"
                            }`}
                          >
                            {badge}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
                  {t("checkout.noEligibleVoucher")}
                </div>
              )}
            </section>

            {/* POINTS (NO CHECKBOX) */}
            <section className="bg-white rounded-2xl shadow-sm p-6 sm:p-7 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-medium">
                    {t("checkout.points")}
                  </h2>

                  {profileLoading ? (
                    <p className="text-sm text-neutral-500 mt-1">
                      {t("checkout.loadingPoints")}
                    </p>
                  ) : (
                    <p
                      className="text-sm text-neutral-500 mt-1"
                      dangerouslySetInnerHTML={{
                        __html: t("checkout.youHavePoints", {
                          points: userPoints,
                          rate: formatMoney(config.points.rate),
                        }),
                      }}
                    />
                  )}

                  <p className="text-xs text-neutral-500 mt-2">
                    {t("checkout.maxPercent", {
                      percent: config.points.maxPercent,
                    })}
                  </p>

                  {profileErr ? (
                    <p className="text-xs text-red-600 mt-2">{profileErr}</p>
                  ) : null}
                </div>

                <div className="text-right">
                  <p className="text-xs text-neutral-500">
                    {t("checkout.discount")}
                  </p>
                  <p className="text-base font-semibold">
                    {formatMoney(pointDiscount)}
                  </p>
                </div>
              </div>

              <div
                className={`rounded-2xl border px-5 py-4 ${
                  userPoints > 0
                    ? "border-neutral-200"
                    : "border-neutral-200 bg-neutral-50"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                      {t("checkout.enterPoints")}
                    </p>
                    <p
                      className="text-xs text-neutral-500 mt-1"
                      dangerouslySetInnerHTML={{
                        __html: t("checkout.maxPoints", {
                          max: maxPointsCanUse,
                        }),
                      }}
                    />
                  </div>

                  <input
                    type="number"
                    min={0}
                    max={maxPointsCanUse}
                    value={pointsInput}
                    disabled={
                      profileLoading || userPoints <= 0 || maxPointsCanUse <= 0
                    }
                    onChange={(e) => {
                      const v = Number(e.target.value || 0);
                      setPointsInput(Math.max(0, Math.min(v, maxPointsCanUse)));
                    }}
                    className="h-12 w-36 rounded-xl border border-neutral-200 px-4 text-sm outline-none focus:border-black disabled:opacity-60"
                  />
                </div>

                {userPoints <= 0 ? (
                  <p className="text-xs text-neutral-500 mt-3">
                    {t("checkout.noPoints")}
                  </p>
                ) : null}
              </div>
            </section>
          </div>

          {/* COL 3: SUMMARY */}
          <div className="lg:col-span-3">
            <div className="sticky top-6 bg-white rounded-2xl shadow-lg p-6 sm:p-7">
              <h2 className="text-lg font-medium mb-5">
                {t("checkout.order")}
              </h2>

              {items.length === 0 ? (
                <div className="text-sm text-neutral-600">
                  {t("checkout.cartEmpty")}{" "}
                  <Link
                    to="/collection"
                    className="underline underline-offset-4"
                  >
                    {t("checkout.backToShop")}
                  </Link>
                  .
                </div>
              ) : (
                <div className="space-y-4 text-sm">
                  {items.map((i, idx) => (
                    <div key={idx} className="flex justify-between items-start">
                      <div className="min-w-0 pr-3">
                        <p className="font-medium truncate">
                          {i.productName ?? `CartLine #${i.variantId}`}
                        </p>
                        <p className="text-xs text-neutral-500 mt-1">
                          {t("checkout.qty", { qty: i.quantity })}
                        </p>
                      </div>
                      <p className="whitespace-nowrap">
                        {formatMoney((i.unitPrice ?? 0) * i.quantity)}
                      </p>
                    </div>
                  ))}

                  <div className="border-t pt-4 space-y-3">
                    <div className="flex justify-between text-neutral-700">
                      <span>{t("checkout.subtotal")}</span>
                      <span>{formatMoney(subtotal)}</span>
                    </div>

                    <div className="flex justify-between text-neutral-700">
                      <span>{t("checkout.shipping")}</span>
                      <span className="whitespace-nowrap">
                        {!address.trim()
                          ? t("checkout.enterAddress")
                          : shippingFee === 0
                            ? t("checkout.free")
                            : formatMoney(shippingFee)}
                      </span>
                    </div>

                    {voucher &&
                    voucher.type !== "freeship" &&
                    voucherDiscount > 0 ? (
                      <div className="flex justify-between text-green-700">
                        <span>
                          {t("checkout.voucherLine", { code: voucher.code })}
                        </span>
                        <span>-{formatMoney(voucherDiscount)}</span>
                      </div>
                    ) : null}

                    {voucher && voucher.type === "freeship" ? (
                      <div className="flex justify-between text-green-700">
                        <span>
                          {t("checkout.voucherLine", { code: voucher.code })}
                        </span>
                        <span>{t("checkout.freeShip")}</span>
                      </div>
                    ) : null}

                    {pointDiscount > 0 ? (
                      <div className="flex justify-between text-green-700">
                        <span>{t("checkout.pointsLine")}</span>
                        <span>-{formatMoney(pointDiscount)}</span>
                      </div>
                    ) : null}

                    <div className="flex justify-between text-lg font-semibold pt-2">
                      <span>{t("checkout.total")}</span>
                      <span className="whitespace-nowrap">
                        {formatMoney(total)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                      {t("checkout.paymentMethod")}
                    </label>

                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as any)}
                      className="mt-2 h-12 w-full rounded-xl border border-neutral-200 px-4 text-sm outline-none focus:border-black"
                    >
                      <option value="COD">{t("checkout.cod")}</option>
                      <option value="TRANSFER">
                        {t("checkout.bankTransfer")}
                      </option>
                    </select>
                  </div>

                  <button
                    disabled={!canCheckout || items.length === 0}
                    onClick={onCheckout}
                    className="mt-5 w-full h-14 rounded-2xl bg-black text-white text-xs tracking-[0.25em] uppercase disabled:opacity-40 hover:opacity-90 transition"
                  >
                    {loading
                      ? t("checkout.processing")
                      : t("checkout.placeOrder")}
                  </button>

                  <p className="text-xs text-neutral-500 text-center mt-3">
                    {t("checkout.slogan")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-lg px-6 py-4 text-sm">
              {t("checkout.overlayProcessing")}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
